import { BackgroundTypes, MapModes, ReactMap } from '@innovyze/inno-map';
import { InspectionStandard, MapStatusType } from '@Types';
import {
	useAuth0,
	useCompanyDateTime,
	useGetMapBackground,
	useIsFeatureEnabled,
	useTzDateTime,
} from '@innovyze/stylovyze';
import {
	selectFullInspection,
	selectFullInspectionIsWaiting,
	useSelectMapInsertInspection,
} from '@Selectors';
import { useFeatureFlagBridge } from '@Utils';

import React from 'react';
import { getFullInsp } from '@Actions/fullInspection.actions';
import { useDispatch } from 'react-redux';
import { useReactMapConfig } from '@innovyze/stylovyze';

export interface Props {
	standard: InspectionStandard;
	inspectionId: string;
	logLevel?: MapStatusType;
}

const MapInsertInspection: React.FC<Props> = ({
	standard,
	inspectionId,
	logLevel = MapStatusType.WARN,
}: Props) => {
	const isTilesV3Enabled = !!useIsFeatureEnabled('info-360-am-map-tiles-v-3');
	const isCloudOsTilesetEnabled = !!useIsFeatureEnabled(
		'i-360-tileset-read-from-cloudos',
	);

	const enableMediaTranscode = !!useIsFeatureEnabled(
		'info-360-media-convert',
	);

	const dispatch = useDispatch();

	const { token } = useAuth0();
	const { formatDate } = useCompanyDateTime();
	const formatDateUTC = useTzDateTime({ timeZone: 'UTC' }).formatDate;
	const fullInspectionIsWaiting = selectFullInspectionIsWaiting();
	const inspection = selectFullInspection();
	const stylovyzeDefaultBackground = useGetMapBackground() as BackgroundTypes;

	const featureFlagBridge = useFeatureFlagBridge();

	const inputs = useSelectMapInsertInspection(
		token,
		inspectionId,
		isTilesV3Enabled,
		isCloudOsTilesetEnabled,
	);

	const background = stylovyzeDefaultBackground ?? BackgroundTypes.OSMStreets;

	React.useEffect(() => {
		if (
			standard !== undefined &&
			!fullInspectionIsWaiting &&
			inspection._id !== inspectionId
		) {
			// Ensure map.reducers are triggered with the appropriate inspection geometry
			dispatch(
				getFullInsp({
					standard,
					inspectionId,
					formatDate,
					formatDateUTC,
					enableMediaTranscode,
				}),
			);
		}
	}, [
		standard,
		inspectionId,
		formatDate,
		dispatch,
		fullInspectionIsWaiting,
		inspection,
	]);

	const reactMapConfig = useReactMapConfig();

	return (
		<ReactMap
			featureFlagBridge={featureFlagBridge}
			config={reactMapConfig}
			mode={MapModes.Navigation}
			dataServices={inputs.sources}
			layerConfig={inputs.layers}
			autoResize={false}
			position={inputs.location?.position ?? undefined}
			showMinZoomWarning={false}
			logLevel={logLevel ?? MapStatusType.WARN}
			background={background}
		/>
	);
};

export default MapInsertInspection;
